// src/components/PatientDetails/panels/FloatingChatPanel.tsx
// -----------------------------------------------------------------------------
// Floating, collapsible chat shell for patient details — wraps ChatPanel (children).
// Uses VideoGlassContainer-style layering (video bg + gradient + glass card)
// when expanded, matching the Bulk Upload Results aesthetic.
// -----------------------------------------------------------------------------

import {
  ChatBubbleOutline,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../../i18n";
import { athenaColors } from "../../../theme/palette";
import AppBackdrop from "../../dashboards/appBackdrop";
import useTutorial from "../../../hooks/useOnboardingTutorial";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface FloatingChatPanelProps {
  children: React.ReactNode;
  /** CSS left offset — controls where the bar starts (e.g. sidebar width + gap) */
  left?: string | number;
  /** CSS right offset — 0 to flush with container edge */
  right?: string | number;
  /** Border-radius for the bottom-right corner to match the parent container */
  bottomRightRadius?: string | number;
}

// Dark overrides matching VideoGlassContainer — ensures all MUI children
// inside the glass card render correctly on the dark background.
const darkOverrides = {
  color: athenaColors.white,
  "& .MuiTypography-root": { color: athenaColors.white },
  "& .MuiTypography-colorTextSecondary, & .MuiTypography-caption": {
    color: `${alpha(athenaColors.white, 0.6)} !important`,
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: alpha(athenaColors.white, 0.07),
    color: athenaColors.white,
    "& fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.3) },
    "&:hover fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.55) },
    "&.Mui-focused fieldset": { borderColor: athenaColors.periwinkle },
  },
  "& .MuiOutlinedInput-input": {
    color: athenaColors.white,
    "&::placeholder": { color: alpha(athenaColors.white, 0.45), opacity: 1 },
  },
  "& .MuiInputLabel-root": {
    color: alpha(athenaColors.white, 0.6),
    "&.Mui-focused": { color: athenaColors.periwinkle },
  },
  "& .MuiIconButton-root": { color: alpha(athenaColors.white, 0.7) },
  "& .MuiSvgIcon-root": { color: alpha(athenaColors.white, 0.5) },
  "& .MuiChip-root": { color: athenaColors.white, borderColor: alpha(athenaColors.periwinkle, 0.3) },
  "& .MuiButton-contained": {
    backgroundColor: athenaColors.royalBlue,
    color: athenaColors.white,
    "&:hover": { backgroundColor: alpha(athenaColors.royalBlue, 0.85) },
  },
  "& .MuiButton-outlined": {
    color: alpha(athenaColors.white, 0.85),
    borderColor: alpha(athenaColors.periwinkle, 0.4),
    "&:hover": { borderColor: athenaColors.periwinkle, backgroundColor: alpha(athenaColors.periwinkle, 0.1) },
  },
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const FloatingChatPanel: React.FC<FloatingChatPanelProps> = ({
  children,
  left,
  right = 0,
  bottomRightRadius,
}) => {
  const [expanded, setExpanded] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const { isActive: tutorialActive } = useTutorial();

  useEffect(() => {
    if (!expanded) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      // Never auto-collapse while the tutorial overlay is active — it uses
      // pointer events that would otherwise trigger this handler on every
      // tooltip/Next-button click, collapsing the panel unexpectedly.
      if (tutorialActive) return;
      const root = panelRef.current;
      if (!root) return;
      const target = event.target as Node;
      if (!root.contains(target)) {
        setExpanded(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [expanded, tutorialActive]);

  return (
    <Box
      ref={panelRef}
      data-tour="floating-chat-panel"
      sx={{

        position: "absolute",
        bottom: 8,
        left: left ?? "50%",
        right,
        ...(left == null && { transform: "translateX(-50%)", width: "min(800px, calc(100% - 40px))" }),
        // Expanded: anchor the top to the parent wrapper so the chat stretches
        // up to the same Y as the patient-name header card (the wrapper's
        // top edge sits flush with the DataPanel's top, where the deep-navy
        // patient-name card lives). On small viewports the prior
        // `clamp(320px, 45vh, 560px)` clamped to the 320 floor, leaving
        // image previews unreadable. Collapsed: keep the 48 px header bar
        // anchored to the bottom. See cursor_logs/036 §11.
        top: expanded ? 8 : "auto",
        height: expanded ? "auto" : "48px",
        transition: "top 0.3s ease, height 0.3s ease",
        zIndex: 1100,
        borderRadius: 2.5,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow: `0 -4px 24px ${alpha(athenaColors.deepNavy, 0.4)}`,
        background: expanded
          ? alpha(athenaColors.deepNavy, 0.55)
          : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.88)} 100%)`,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: `1px solid ${alpha(expanded ? athenaColors.white : theme.palette.secondary.main, 0.22)}`,
      }}
    >
      {/* Background layers (only when expanded) — uses the sidebar gradient
          for a professional look. Video element is retained but hidden. */}
      {expanded && (
        <>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(160deg, ${alpha(athenaColors.deepNavy, 0.95)} 0%, ${alpha(
                athenaColors.deepNavy,
                0.85
              )} 50%, ${alpha(athenaColors.royalBlue, 0.78)} 100%)`,
              zIndex: 0,
            }}
          />
          {/* opacity 0: invisible, and as a <video> it still decoded every
              frame. Kept as a still so the intent survives at no cost. */}
          <AppBackdrop sx={{ opacity: 0 }} />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(160deg, ${alpha(athenaColors.deepNavy, 0.95)} 0%, ${alpha(
                athenaColors.deepNavy,
                0.85
              )} 50%, ${alpha(athenaColors.royalBlue, 0.78)} 100%)`,
              zIndex: 1,
            }}
          />
        </>
      )}

      {/* Content — sits directly on top, no inner card margin */}
      <Box
        sx={[
          {
            position: "relative",
            zIndex: 2,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          },
          expanded ? (darkOverrides as Record<string, unknown>) : {},
        ]}
      >
        {/* Header bar — always keeps the collapsed gradient for consistency */}
        <Box
          data-tour="floating-chat-bar"
          onClick={() => setExpanded((v) => !v)}
          sx={{
            height: 44,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            cursor: "pointer",
            borderBottom: expanded
              ? `1px solid ${alpha(athenaColors.white, 0.1)}`
              : "none",
            ...(expanded && {
              background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.88)} 100%)`,
              borderRadius: "20px 20px 0 0",
              position: "relative",
              zIndex: 3,
            }),
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <ChatBubbleOutline
              sx={{ color: athenaColors.white, fontSize: 20 }}
              aria-hidden
            />
            <Typography
              component="span"
              sx={{
                color: athenaColors.white,
                fontSize: "0.82rem",
                fontWeight: 600,
              }}
            >
              {t("patient_layout.chat")}
            </Typography>
          </Box>
          <IconButton
            data-tour="floating-chat-expand"
            aria-label={
              expanded
                ? t("patient_layout.collapse_chat")
                : t("patient_layout.expand_chat")
            }
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setExpanded((open) => !open);
            }}
            sx={{
              color: athenaColors.white,
              "&:hover": {
                backgroundColor: alpha(athenaColors.periwinkle, 0.15),
              },
            }}
          >
            {expanded ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
          </IconButton>
        </Box>

        {/* Chat content — full remaining space */}
        <Box
          sx={{
            flex: expanded ? 1 : 0,
            minHeight: 0,
            overflow: expanded ? "auto" : "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default FloatingChatPanel;
